import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, Settings, Plus, ClipboardCheck } from 'lucide-react';
import { useOrganization, Organization } from '../../contexts/OrganizationContext';

interface OrganizationSwitcherSectionProps {
  /** Called after any item is clicked, so the parent dropdown can close itself. */
  onAfterAction?: () => void;
}

/**
 * Organization list + actions, rendered inside the user dropdown. This is a
 * presentational section only — it has no trigger button, container chrome, or
 * outside-click/escape handling, since the host dropdown (UserMenu) owns those.
 */
const OrganizationSwitcherSection: React.FC<OrganizationSwitcherSectionProps> = ({
  onAfterAction,
}) => {
  const {
    organizations,
    currentOrganization,
    switchOrganization,
    isLoading,
    onboardingComplete,
  } = useOrganization();
  const navigate = useNavigate();

  const handleOrgSelect = (org: Organization) => {
    switchOrganization(org.id);
    navigate('/repositories');
    onAfterAction?.();
  };

  const handleManageOrg = () => {
    if (!currentOrganization) return;
    navigate(`/organizations/${currentOrganization.id}/settings`);
    onAfterAction?.();
  };

  const handleCreateOrg = () => {
    navigate('/organizations/new');
    onAfterAction?.();
  };

  const handleCompliance = () => {
    navigate('/compliance');
    onAfterAction?.();
  };

  if (isLoading || !currentOrganization) {
    return null;
  }

  return (
    <div role="group" aria-label="Switch organization">
      <div className="px-3 pt-2 pb-1 text-[10px] font-medium uppercase tracking-wider text-ui-text-tertiary">
        Organisation
      </div>

      {/* Organization List */}
      <div className="max-h-60 overflow-y-auto">
        {organizations.map((org) => (
          <button
            key={org.id}
            onClick={() => handleOrgSelect(org)}
            className={`w-full flex items-center gap-3 px-3 py-2
                       hover:bg-ui-background-tertiary
                       transition-colors duration-150 text-left
                       ${org.id === currentOrganization.id ? 'bg-brand-primary/5 dark:bg-brand-primary/10' : ''}`}
            role="option"
            aria-selected={org.id === currentOrganization.id}
          >
            <div className="w-7 h-7 bg-ui-background-tertiary rounded flex items-center justify-center flex-shrink-0">
              <Building2 className="w-4 h-4 text-ui-text-tertiary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ui-text-primary truncate">
                {org.name}
              </p>
              {org.current_user_role && (
                <p className="text-xs text-ui-text-tertiary capitalize">
                  {org.current_user_role}
                </p>
              )}
            </div>
            {org.id === currentOrganization.id && (
              <Check className="w-4 h-4 text-brand-primary dark:text-brand-primaryDarkMode flex-shrink-0" />
            )}
          </button>
        ))}
      </div>

      {/* Actions */}
      <button
        onClick={handleCompliance}
        className="flex items-center w-full text-left px-3 py-2 text-sm
                   text-ui-text-secondary
                   hover:bg-ui-background-tertiary transition-colors"
      >
        <ClipboardCheck size={14} className="mr-1.5" />
        Compliance
      </button>
      {onboardingComplete && (
        <button
          onClick={handleCreateOrg}
          className="flex items-center w-full text-left px-3 py-2 text-sm
                     text-ui-text-secondary
                     hover:bg-ui-background-tertiary transition-colors"
        >
          <Plus size={14} className="mr-1.5" />
          Create New Organization
        </button>
      )}
      <button
        onClick={handleManageOrg}
        className="flex items-center w-full text-left px-3 py-2 text-sm
                   text-ui-text-secondary
                   hover:bg-ui-background-tertiary transition-colors"
      >
        <Settings size={14} className="mr-1.5" />
        Manage Organization
      </button>
    </div>
  );
};

export default OrganizationSwitcherSection;
